import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { perfHubPublicInvite, perfHubPublicOAuthUrl } from '../api/api';
import './BrandDashboard.css';
import './PerformanceHub.css';

/**
 * Public page — no Milan login. Creator lands from email magic link ?t=...
 */
function PerformanceConnect() {
  const [params] = useSearchParams();
  const token = params.get('t') || '';
  const success = params.get('success');
  const err = params.get('error');

  const [preview, setPreview] = useState(null);
  const [loadErr, setLoadErr] = useState('');
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    if (!token || err) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await perfHubPublicInvite(token);
        if (!cancelled) setPreview(res.data);
      } catch (e) {
        if (!cancelled) setLoadErr(e?.response?.data?.detail || 'Invalid link');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, err, success]);

  const startOAuth = async () => {
    setConnecting(true);
    setLoadErr('');
    try {
      const res = await perfHubPublicOAuthUrl(token);
      const url = res.data?.auth_url;
      if (url) window.location.href = url;
      else setLoadErr('Could not start Instagram');
    } catch (e) {
      setLoadErr(e?.response?.data?.detail || 'Could not start Instagram');
    } finally {
      setConnecting(false);
    }
  };

  return (
    <div className="ph-connect-page">
      <div className="section-card ph-connect-inner">
        <span className="ph-connect-badge">Instagram · Milan AI</span>
        <h1 className="ph-connect-title">Connect your Instagram</h1>
        {success && (
          <p className="ph-connect-success">
            You’re connected. The brand can now see verified insights for the post they listed.
          </p>
        )}
        {preview?.post_guard && (
          <div
            className={`ph-connect-pg ph-connect-pg--${
              preview.post_guard.readiness === 'good' || preview.post_guard.readiness === 'green'
                ? 'good'
                : preview.post_guard.readiness === 'not_connected'
                  ? 'not-connected'
                  : preview.post_guard.readiness === 'not_live'
                    ? 'not-live'
                    : 'review'
            }`}
          >
            <strong>Post check: {preview.post_guard.readiness_label}</strong>
            <p className="ph-connect-text" style={{ marginTop: 8, marginBottom: 0 }}>
              {preview.post_guard.creator_fix_message}
            </p>
            {preview.post_guard.readiness === 'not_connected' && (
              <p className="ph-connect-pg-verify">
                Connect Instagram below so the brand can analyze your post.
              </p>
            )}
            {preview.post_guard.readiness === 'not_live' && (
              <p className="ph-connect-pg-verify">
                Your post must be live and synced before the brand can treat this as complete.
              </p>
            )}
            {preview.post_guard.readiness !== 'good' &&
              preview.post_guard.readiness !== 'green' &&
              preview.post_guard.readiness !== 'not_live' &&
              preview.post_guard.readiness !== 'not_connected' && (
              <p className="ph-connect-pg-verify">
                The brand will re-verify your live post before any payment. This is not a payment
                guarantee from Milan AI.
              </p>
            )}
            <p className="ph-connect-pg-legal">
              Informational only—not legal or payout advice. Milan AI does not guarantee compliance.
            </p>
          </div>
        )}
        {err && <p className="ph-connect-error">{decodeURIComponent(err)}</p>}
        {loadErr && <p className="ph-connect-error">{loadErr}</p>}
        {preview && !success && !err && (
          <>
            <p className="ph-connect-text">
              <strong>{preview.brand_name}</strong> invited you for <strong>{preview.campaign_name}</strong>.
            </p>
            {preview.instagram_handle_expected && (
              <p className="ph-connect-text">
                Connect the account matching <strong>@{preview.instagram_handle_expected}</strong>.
              </p>
            )}
            <button
              type="button"
              className="btn-primary"
              style={{ marginTop: 12 }}
              disabled={connecting}
              onClick={startOAuth}
            >
              {connecting ? 'Redirecting…' : 'Connect with Instagram'}
            </button>
            <p className="ph-connect-text" style={{ marginTop: 16, fontSize: 13 }}>
              You will sign in on Instagram’s site. Milan AI does not receive your Instagram password.
            </p>
          </>
        )}
        {!token && !success && !err && <p className="ph-connect-error">Missing invite token.</p>}
        <p className="ph-connect-foot">
          <Link to="/">← Back to home</Link>
        </p>
      </div>
    </div>
  );
}

export default PerformanceConnect;
